import { api } from 'api';
import { getRegionCode } from 'common/selectors';
import {
  getReviewGroupSorting,
  getReviewsPerPage,
  getReviewsProductId,
  getReviewsSorting,
} from 'features/reviews/selectors';
import log from 'loglevel';
import { call, cancelled, put, select, takeLatest } from 'redux-saga/effects';
import { EffectR } from 'types/utils';
import {
  ReviewsPhotosPaginationProps,
  ReviewsPhotosRequestProps,
} from 'features/reviews-photos/types/fetch-models';
import { FETCH_REVIEWS_PHOTOS_PAGE } from '../actions/reviews-photos';
import { fetchReviewsPhotosPage } from '../utils/api';
import { SagaIterator } from 'redux-saga';

export function* runReviewsPhotosPaginationNextFetch(props: ReviewsPhotosPaginationProps): SagaIterator {
  const productId = (yield select(getReviewsProductId)) as EffectR.Select<typeof getReviewsProductId>;
  const { currentPage } = props;

  if (typeof productId !== 'string') {
    yield put(
      FETCH_REVIEWS_PHOTOS_PAGE.failure(new TypeError('Failed to extract productId from reviews photos')),
    );
    return;
  }

  const reviewsSort = (yield select(getReviewsSorting)) as EffectR.Select<typeof getReviewsSorting>;
  const sort: 'created_at:desc' | 'created_at:asc' =
    reviewsSort === 'created_at:asc' || reviewsSort === 'created_at:desc' ? reviewsSort : 'created_at:desc';
  const reviewListType = (yield select(getReviewGroupSorting)) as EffectR.Select<
    typeof getReviewGroupSorting
  >;
  const perPage = (yield select(getReviewsPerPage)) as EffectR.Select<typeof getReviewsPerPage>;
  const regionCode = (yield select(getRegionCode)) as ReturnType<typeof getRegionCode>;

  const fetchProps: ReviewsPhotosRequestProps = {
    productId,
    page: String(currentPage + 1),
    perPage: String(perPage),
    sort: sort,
    reviewListType,
    preview: 'false',
    regionCode,
  };

  const abortController = yield* getAbortController();

  try {
    const data = (yield call(fetchReviewsPhotosPage, fetchProps, {
      cancelToken: abortController.token,
    })) as EffectR.Call<typeof fetchReviewsPhotosPage>;

    yield put(FETCH_REVIEWS_PHOTOS_PAGE.success(data));
  } catch (err) {
    log.warn('[reviews-photos] get-reviews-photos request failed', err);
    if (err instanceof Error) {
      yield put(FETCH_REVIEWS_PHOTOS_PAGE.failure(err));
    }
  } finally {
    if ((yield cancelled()) as boolean) {
      abortController.cancel();
      yield put(FETCH_REVIEWS_PHOTOS_PAGE.cancel());
    }
  }
}
function* getAbortController() {
  return (yield call([api, 'abortController'])) as EffectR.Call<(typeof api)['abortController']>;
}

function* handleReviewsPhotosPageRequest({ payload }: { payload: ReviewsPhotosPaginationProps }) {
  yield call(runReviewsPhotosPaginationNextFetch, payload);
}

export function* reviewsPhotosSaga() {
  yield takeLatest(FETCH_REVIEWS_PHOTOS_PAGE.request, handleReviewsPhotosPageRequest);
}
